import useLocalStorage from 'hooks/useLocalStorageState'
import React from 'react'
import subsidiariesApi from 'services/api/subsidiaries'
import userApi from 'services/api/user'

// Add jsDoc
/**
 *
 * @typedef Hotel
 * @property {string} hotelName
 * @property {string} bookingEngine
 * @property {string} chainCode
 * @property {string} parentChainCode
 * @property {string} textMarketingUrlKey
 * @property {string} addedOn
 * @property {number} addedBy
 * @property {string} updatedOn
 * @property {number} updatedBy
 * @property {string} id
 * @property {number} numberOfRooms
 * @property {string} pms
 * @property {boolean} isMissingSomeInformation
 * @property {number} displayId
 * @property {boolean} isDeleted
 * @property {number} status
 * @property {object[]} subsidiaryPmsUsers
 * @property {number} subsidiaryPmsUsers.id
 * @property {string} subsidiaryPmsUsers.subsidiaryId
 * @property {string} subsidiaryPmsUsers.email
 * @property {string} subsidiaryPmsUsers.firstName
 * @property {string} subsidiaryPmsUsers.lastName
 * @property {string} subsidiaryPmsUsers.birthDate
 * @property {string} subsidiaryPmsUsers.addedOn
 * @property {number} subsidiaryPmsUsers.addedBy
 * @property {string} merchantName
 * @property {object[]} inviteBlockOn
 * @property {string} inviteBlockOn.addedBy
 * @property {string} inviteBlockOn.addedOn
 * @property {string} inviteBlockOn.reason
 * @property {boolean} pmsSyncIsConnected
 * @property {boolean} isServiceStarted
 * @property {boolean} isCampaignsEnabled
 * @property {boolean} isTcrActivated
 * @property {boolean} isPhoneEnabled
 * @property {object[]} tags
 * @property {number} tags.id
 * @property {string} tags.name
 * @property {string} tags.addedBy
 * @property {string} tags.addedOn
 * @property {string} processKind
 *
 * @typedef {('hotel' | 'group' | 'brand' | 'addedOn' | 'stateTimeZone' | 'customerSuccessManager')} SortParam
 *
 * @typedef {('up' | 'down')} SortDirection
 *
 * @callback SetSortParam
 * @param {SortParam} sortParam
 *
 */
export function useHotelsData(type) {
  /**
   * @type {[Hotel[], React.Dispatch<React.SetStateAction<Hotel[]>>]} state
   */
  const [hotels, setHotels] = React.useState([])
  const [customerSuccessManagers, setCustomerSuccessManagers] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = useLocalStorage(
    `hotelsByCsmFilterSearchQuery`,
    ''
  )

  const [sortParam, setSortParam] = useLocalStorage(`hotelsByCsmFilterSortParam`, 'hotel')
  const [sortDirection, setSortDirection] = useLocalStorage(
    `hotelsByCsmFilterSortDirection`,
    'down'
  )

  const [selectedGroup, setSelectedGroup] = useLocalStorage(
    `hotelsByCsmFilterGroup`,
    'all'
  )
  const [selectedParentChain, setSelectedParentChain] = useLocalStorage(
    `hotelsByCsmFilterParentChain`,
    'all'
  )
  const [selectedState, setSelectedState] = useLocalStorage(
    `hotelsByCsmFilterState`,
    'all'
  )
  const [selectedTags, setSelectedTags] = useLocalStorage(`hotelsByCsmFilterTags`, [])
  const [selectedCustomerSuccessManagers, setSelectedCustomerSuccessManagers] =
    useLocalStorage('hotelsByCsmFilterCustomerSuccessManagers', [])

  function clearFilters() {
    setSelectedGroup('all')
    setSelectedParentChain('all')
    setSelectedState('all')
    setSelectedTags([])
    setSelectedCustomerSuccessManagers([])
    setSearchQuery('')
  }

  async function getCustomerSuccessManagers() {
    try {
      const csmData = await userApi.getCustomerSuccessManagers()
      setCustomerSuccessManagers(csmData)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    async function getData() {
      setIsLoading(true)
      setHotels([])

      try {
        const hotelsData = await subsidiariesApi.getHotelList({ type })
        const csmData = await userApi.getCustomerSuccessManagers()
        setHotels(hotelsData)
        setCustomerSuccessManagers(csmData)
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    setIsLoading(true)
    setHotels([])

    getData()
  }, [type])

  const sortedAndFilteredHotels = React.useMemo(() => {
    const filteredHotels = hotels
      .filter(h => (selectedGroup === 'all' ? true : h.merchantName === selectedGroup))
      .filter(h =>
        selectedParentChain === 'all' ? true : h.parentChainCode === selectedParentChain
      )
      .filter(h => {
        if (selectedState === 'all') {
          return true
        }

        return h.stateCode === selectedState
      })
      .filter(h => {
        if (selectedTags.length === 0) {
          return true
        }

        return h.csmTags.some(tag => selectedTags.includes(tag.name))
      })
      .filter(h => {
        if (selectedCustomerSuccessManagers.length === 0) {
          return true
        }

        const isUnnasigned =
          selectedCustomerSuccessManagers.includes('unassigned') && !h.csmUser

        // if selected csm's csm.assignedSubsidiaryIds includes h.id return true or false
        const selectedCsms = customerSuccessManagers.filter(csm =>
          selectedCustomerSuccessManagers.includes(csm.id.toString())
        )

        const selectedCsmsSubsidiaryIds = selectedCsms.flatMap(
          csm => csm.assignedSubsidiaryIds
        )

        if (selectedCsmsSubsidiaryIds.includes(h.id) && !isUnnasigned) {
          return true
        } else if (!selectedCsmsSubsidiaryIds.includes(h.id) && isUnnasigned) {
          return true
        }

        return false
      })
      .filter(h => h.searchString?.toLowerCase().includes(searchQuery.toLowerCase()))

    const sorttedHotels = filteredHotels.sort((a, b) => {
      switch (sortParam) {
        case 'hotel':
          return a.displayId - b.displayId
        case 'group':
          return a.merchantName?.toUpperCase() > b.merchantName?.toUpperCase() ? 1 : -1
        case 'brand':
          return a.parentChainCode?.toUpperCase() > b.parentChainCode?.toUpperCase()
            ? 1
            : -1
        case 'addedOn':
          return new Date(a.addedOn) - new Date(b.addedOn)
        case 'updatedOn':
          return new Date(a.updatedOn) - new Date(b.updatedOn)
        case 'status':
          return a.status.toUpperCase() > b.status.toUpperCase() ? 1 : -1
        default:
          return 0
      }
    })

    return sortDirection === 'down' ? sorttedHotels : sorttedHotels.reverse()
  }, [
    customerSuccessManagers,
    hotels,
    searchQuery,
    selectedCustomerSuccessManagers,
    selectedGroup,
    selectedParentChain,
    selectedState,
    selectedTags,
    sortDirection,
    sortParam
  ])

  return {
    sortedAndFilteredHotels,
    hotels,
    customerSuccessManagers,
    getCustomerSuccessManagers,
    isLoading,
    sort: {
      sortParam,
      setSortParam,
      sortDirection,
      setSortDirection
    },
    filter: {
      selectedGroup,
      setSelectedGroup,
      selectedParentChain,
      setSelectedParentChain,
      selectedState,
      setSelectedState,
      selectedTags,
      setSelectedTags,
      selectedCustomerSuccessManagers,
      setSelectedCustomerSuccessManagers,
      clearFilters
    },
    search: {
      searchQuery,
      setSearchQuery
    }
  }
}
